import React from 'react';
import { cn } from '@core/lib/utils';
import { useChatCopy } from '../../config';

interface ThinkingIndicatorProps {
  sender: 'stevens' | 'claude' | 'cristina';
  /** Persona display name (e.g. "Elizabeth"). When omitted, falls back to the brand’s coach fallback name. */
  coachName?: string;
  message?: string;
  className?: string;
}

const PulsingDots = React.memo(function PulsingDots() {
  return (
    <span className="inline-flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-chat-text-muted animate-bounce"
          style={{ animationDelay: `${i * 150}ms`, animationDuration: '1.4s' }}
        />
      ))}
    </span>
  );
});

export const ThinkingIndicator = React.memo(function ThinkingIndicator({
  coachName,
  message,
  className,
}: ThinkingIndicatorProps) {
  // #1302 Guard B: the coach-voice progress copy is brand copy — it arrives
  // via the BrandChatConfig copy pack (LMC values identical to the
  // pre-extraction literals, including the same timing thresholds).
  const thinkingCopy = useChatCopy().thinking;

  // Progressive messages based on time (preserved from previous behavior).
  // #2109 R3-B24: the timers advance a STEP, and the text is read from the
  // live pack at render, so a language switch mid-wait re-renders the line
  // in the new language instead of keeping the text captured at mount.
  const [step, setStep] = React.useState(-1);

  React.useEffect(() => {
    const timers = thinkingCopy.progressive.map(({ atMs }, i) =>
      setTimeout(() => setStep(i), atMs),
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the schedule is static per build; only the text is language-dependent
  }, []);

  const currentMessage = step < 0
    ? message ?? thinkingCopy.initialMessage
    : thinkingCopy.progressive[step]?.text ?? thinkingCopy.initialMessage;

  const displayName = coachName ?? thinkingCopy.fallbackCoachName;

  return (
    <div
      className={cn('flex items-center gap-2.5 text-[13px] text-chat-text-muted', className)}
      role="status"
      aria-live="polite"
    >
      <span className="font-hand text-body text-chat-text">{displayName}</span>
      <PulsingDots />
      <span className="italic">{currentMessage}…</span>
    </div>
  );
});
